import { capitalize } from '~/lib';
import { ListingFeatureStatEnum } from '~/pages/FeatureListing/enums';
import {
  FeaturedListingRulesResponseType,
  ListingFeatureType,
} from '~/types/featureListingTypes';

// `listingRules` comes back keyed by the feature name, so flatten it into a
// list the cards can iterate over.
export const buildListingFeatures = (
  data?: FeaturedListingRulesResponseType,
): ListingFeatureType[] =>
  Object.entries(data?.listingRules ?? {}).map(([key, rule]) => ({
    ...rule,
    key,
    title: capitalize(key),
    stats: [
      {
        key: ListingFeatureStatEnum.IMPRESSIONS,
        multiplier: rule.impressionsCount,
      },
      { key: ListingFeatureStatEnum.VIEWS, multiplier: rule.viewsCount },
      { key: ListingFeatureStatEnum.CALLS, multiplier: rule.leadsCount },
    ],
  }));

export const findListingFeature = (
  data: FeaturedListingRulesResponseType | undefined,
  key: string | undefined,
): ListingFeatureType | undefined =>
  key
    ? buildListingFeatures(data).find((feature) => feature.key === key)
    : undefined;

// The API leaves out the price/duration it does not charge for, so render a
// missing value as a plain zero instead of hiding the price line.
export const getFeatureFees = (feature: ListingFeatureType | null) =>
  feature?.fees ?? 0;

export const getFeatureAmount = (feature: ListingFeatureType | null) =>
  feature?.amount ?? 0;

export const getFeatureDuration = (feature: ListingFeatureType | null) =>
  feature?.duration ?? 0;

// The listing page lives in another project, so a successful feature is handed
// over through localStorage: it reads this key to show the success banner and
// clears it afterwards.
export const FEATURED_LISTING_SUCCESS_KEY = 'featuredListingSuccess';

export type FeaturedListingSuccessType = {
  id: string;
  // `points` when the fees were deducted from the user's balance, `money` when
  // the feature was paid for through the checkout page.
  costType: 'points' | 'money';
  cost: number;
  duration: number;
};

export const markListingFeatured = ({
  id,
  costType,
  feature,
}: {
  id?: string | number;
  costType: FeaturedListingSuccessType['costType'];
  feature: ListingFeatureType | null;
}) => {
  if (typeof window === 'undefined' || !id) return;

  const payload: FeaturedListingSuccessType = {
    id: String(id),
    costType,
    cost:
      costType === 'points'
        ? getFeatureFees(feature)
        : getFeatureAmount(feature),
    duration: getFeatureDuration(feature),
  };

  try {
    localStorage.setItem(FEATURED_LISTING_SUCCESS_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to store the featured listing flag:', error);
  }
};
